import { createAction, createSlice } from '@reduxjs/toolkit';

import { resetStateAction } from '!core/redux';

const selectedTradeInitialState = {
  timestamp: null,
  events: [],
  docs: [],
  isLoadingEvents: false,
  isLoadingDocs: false,
  bookType: '',
};

const initialState = {
  daily: { data: [] },
  history: { data: [] },
  dailyStockExchange: { data: [], isLoading: false },
  historyStockExchange: { data: [], isLoading: false },
  dailyFunds: { data: [], isLoading: false },
  historyFunds: { data: [], isLoading: false },
  selectedTrade: selectedTradeInitialState,
  currentTab: 0,
};

const logoutSuccessAction = createAction('logoutSlice/logoutSuccess');

const getCurrentTradeMainInfo = (state, ordBookKey, bookType, market) => {
  let tradingSection = '';

  if (market === 'stockExchange' && bookType === 'B') {
    tradingSection = 'dailyStockExchange';
  } else if (market === 'stockExchange' && bookType === 'H') {
    tradingSection = 'historyStockExchange';
  } else if (market === 'funds' && bookType === 'B') {
    tradingSection = 'dailyFunds';
  } else if (market === 'funds' && bookType === 'H') {
    tradingSection = 'historyFunds';
  } else if (market === 'primary' && bookType === 'B') {
    tradingSection = 'daily';
  } else {
    tradingSection = 'history';
  }

  const mainInfo = state[tradingSection].data.find(
    (trade) => trade.ordBookKey === ordBookKey,
  );

  return mainInfo;
};

const clearState = () => ({ ...initialState });

export const tradingSlice = createSlice({
  name: 'tradingSlice',
  initialState,
  reducers: {
    tradingDailyRequest: (state) => {
      state.daily.isLoading = true;
    },
    tradingDailySucess: (state, action) => {
      state.daily.data = action.payload;
      state.daily.isLoading = false;
    },
    tradingDailyFailure: (state) => {
      state.daily.isLoading = false;
    },
    tradingHstRequest: (state) => {
      state.history.isLoading = true;
    },
    tradingHstSucess: (state, action) => {
      state.history.data = action.payload;
      state.history.isLoading = false;
    },
    tradingHstFailure: (state) => {
      state.history.isLoading = false;
    },
    tradingDailyStockExchangeRequest: (state) => {
      state.dailyStockExchange.isLoading = true;
      state.selectedTrade = selectedTradeInitialState;
    },
    tradingDailyStockExchangeSuccess: (state, action) => {
      state.dailyStockExchange.data = action.payload;

      if (action.payload.length > 0)
        state.selectedTrade = {
          ...selectedTradeInitialState,
          ...action.payload[0],
        };

      state.dailyStockExchange.isLoading = false;
    },
    tradingDailyStockExchangeFailure: (state) => {
      state.dailyStockExchange.isLoading = false;
    },
    tradingHstStockExchangeRequest: (state) => {
      state.historyStockExchange.isLoading = true;
      state.selectedTrade = selectedTradeInitialState;
    },
    tradingHstStockExchangeSucess: (state, action) => {
      state.historyStockExchange.data = action.payload;
      state.historyStockExchange.isLoading = false;
    },
    tradingHstStockExchangeFailure: (state) => {
      state.historyStockExchange.isLoading = false;
    },
    tradingFundsDailyRequest: (state) => {
      state.dailyFunds.isLoading = true;
      state.selectedTrade = selectedTradeInitialState;
    },
    tradingDailyFundsSucess: (state, action) => {
      state.dailyFunds.data = action.payload;
      state.dailyFunds.isLoading = false;
    },
    tradingDailyFundsFailure: (state) => {
      state.dailyFunds.isLoading = false;
    },
    tradingHstFundsRequest: (state) => {
      state.historyFunds.isLoading = true;
      state.selectedTrade = selectedTradeInitialState;
    },
    tradingHstFundsSucess: (state, action) => {
      state.historyFunds.data = action.payload;
      state.historyFunds.isLoading = false;
    },
    tradingHstFundsFailure: (state) => {
      state.historyFunds.isLoading = false;
    },
    tradeEventsRequest: (state, action) => {
      const { ordBookKey, bookType, market } = action.payload;

      const currentTradeMainInfo = getCurrentTradeMainInfo(
        state,
        ordBookKey,
        bookType,
        market,
      );

      state.selectedTrade = {
        ...currentTradeMainInfo,
        timestamp: null,
        events: [],
        docs: [],
        isLoadingEvents: true,
        isLoadingDocs: false,
        bookType: '',
      };
    },
    tradeEventsSucess: (state, action) => {
      state.selectedTrade.timestamp = action.payload.timestamp ?? null;
      state.selectedTrade.events = action.payload.events;
      state.selectedTrade.isLoadingEvents = false;
      state.selectedTrade.bookType = action.payload.bookType;
    },
    tradeEventsFailure: (state) => {
      state.selectedTrade.isLoadingEvents = false;
    },
    loadDocListRequest: (state) => {
      state.selectedTrade.isLoadingDocs = true;
    },
    loadDocListSuccess: (state, action) => {
      state.selectedTrade = {
        ...state.selectedTrade,
        docs: action.payload.docs,
        isLoadingDocs: false,
      };
    },
    loadDocListFailure: (state) => {
      state.selectedTrade.isLoadingDocs = false;
    },
    selectTrade: (state, action) => {
      if (Object.keys(action.payload).length === 0) {
        state.selectedTrade = selectedTradeInitialState;
      } else {
        const { ordBookKey, bookType, market } = action.payload;

        const currentTradeMainInfo = getCurrentTradeMainInfo(
          state,
          ordBookKey,
          bookType,
          market,
        );

        state.selectedTrade = {
          ...currentTradeMainInfo,
          timestamp: null,
          events: [],
          docs: [],
          isLoadingEvents: false,
          isLoadingDocs: false,
          bookType,
        };
      }
    },
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logoutSuccessAction, clearState)
      .addCase(resetStateAction, clearState);
  },
});
